import { type PhilatelicItem } from '@briefmarken_katalog/types';
import { getAllItems, getAllSuggestions } from '$lib/services/philatelic_items';
import { listUpdateTriggered } from '$lib/stores/trigger_list_update';
import { user, doLogin } from '$lib/stores/auth';
import { get } from 'svelte/store';
import { type SearchFilter, defaultSearchFilter, applyFilter } from './SearchFilter';

export class ItemAndSuggestionListsModel {
  items = $state<PhilatelicItem[] | null>(null);
  suggestions = $state<PhilatelicItem[] | null>(null);
  error = $state<string | null>(null);
  loading = $state(true);
  ownItemsOnly = $state(false);
  ownSuggestionsOnly = $state(false);
  inReviewByMe = $state(false);
  itemFilter = $state<SearchFilter>(defaultSearchFilter());
  suggestionFilter = $state<SearchFilter>(defaultSearchFilter());

  private _user = $state(get(user));

  readonly canFilterOwn = $derived(
    this._user?.role === 'Admin' ||
    this._user?.role === 'Contributor' ||
    this._user?.role === 'Reviewer'
  );

  readonly canFilterInReview = $derived(
    this._user?.role === 'Admin' ||
    this._user?.role === 'Reviewer'
  );

  readonly canAddSuggestion = $derived(
    this._user?.role === 'Admin' ||
    this._user?.role === 'Reviewer' ||
    this._user?.role === 'Contributor'
  );

  readonly visibleItems = $derived.by(() => {
    let result = this.items ?? [];
    if (this.ownItemsOnly && this._user)
      result = result.filter(s => s.review.suggestedBy === this._user!.id);
    result = applyFilter(result, this.itemFilter, this._user?.id ?? null);
    return this.items === null ? null : result;
  });

  readonly visibleSuggestions = $derived.by(() => {
    const userId = this._user?.id;
    let result = this.suggestions ?? [];
    if (this.ownSuggestionsOnly && userId)
      result = result.filter(s => s.review.suggestedBy === userId);
    if (this.inReviewByMe && userId)
      result = result.filter(s => s.review.reviewedBy === userId && (s.review.state === 'InReview' || s.review.state === 'Rejected'));
    result = applyFilter(result, this.suggestionFilter, userId ?? null);
    return this.suggestions === null ? null : result;
  });

  resetItemFilter() {
    this.itemFilter = defaultSearchFilter();
  }

  resetSuggestionFilter() {
    this.suggestionFilter = defaultSearchFilter();
  }

  constructor() {
    $effect(() => {
      return user.subscribe(value => {
        this._user = value;
      });
    });

    $effect(() => {
      return listUpdateTriggered.subscribe(() => {
        this.fetchData();
      });
    });
  }

  private fetchGeneration = 0;

  private async fetchData() {
    const gen = ++this.fetchGeneration;
    this.loading = true;
    try {
      const [items, suggestions] = await Promise.all([
        getAllItems(doLogin),
        getAllSuggestions(doLogin),
      ]);
      if (gen !== this.fetchGeneration) return;
      this.items = items;
      this.suggestions = suggestions;
    } catch (err: any) {
      if (gen !== this.fetchGeneration) return;
      this.error = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      if (gen === this.fetchGeneration) this.loading = false;
    }
  }
}
